import { FilterService } from 'primereact/api';

export interface IGeographyEntry {
  code: string;
  // Absent while the Country/Region/Destination lists are not loaded, or when no name is known.
  name?: string;
}

export interface ILookupItem {
  code: string;
  name: string;
}

export interface IMatchRange {
  start: number;
  end: number;
}

export const GEOGRAPHY_MATCH_MODES = {
  contains: 'geographyContains',
  startsWith: 'geographyStartsWith',
  endsWith: 'geographyEndsWith',
  equals: 'geographyEquals',
  notContains: 'geographyNotContains',
  notEquals: 'geographyNotEquals',
} as const;

type TextMatchKind = 'contains' | 'startsWith' | 'endsWith' | 'equals';

const POSITIVE_KIND_BY_MODE: Record<string, TextMatchKind> = {
  [GEOGRAPHY_MATCH_MODES.contains]: 'contains',
  [GEOGRAPHY_MATCH_MODES.startsWith]: 'startsWith',
  [GEOGRAPHY_MATCH_MODES.endsWith]: 'endsWith',
  [GEOGRAPHY_MATCH_MODES.equals]: 'equals',
  [GEOGRAPHY_MATCH_MODES.notContains]: 'contains',
  [GEOGRAPHY_MATCH_MODES.notEquals]: 'equals',
};

const NEGATIVE_MODES = new Set<string>([GEOGRAPHY_MATCH_MODES.notContains, GEOGRAPHY_MATCH_MODES.notEquals]);

export function isNegativeGeographyMode(matchMode: string): boolean {
  return NEGATIVE_MODES.has(matchMode);
}

export function isGeographyMode(matchMode: string): boolean {
  return matchMode in POSITIVE_KIND_BY_MODE;
}

export function toGeographyEntries(codes: string[], lookup: ILookupItem[]): IGeographyEntry[] {
  const nameByCode = new Map(lookup.map((item) => [item.code, item.name]));
  return codes.map((code) => ({ code, name: nameByCode.get(code) }));
}

export function joinGeographyCodes(entries: IGeographyEntry[]): string {
  return entries.map((entry) => entry.code).join(', ');
}

function normalise(value: string): string {
  return value.toLowerCase();
}

function textMatches(text: string, term: string, kind: TextMatchKind): boolean {
  const haystack = normalise(text);
  const needle = normalise(term);
  switch (kind) {
    case 'contains': {
      return haystack.includes(needle);
    }
    case 'startsWith': {
      return haystack.startsWith(needle);
    }
    case 'endsWith': {
      return haystack.endsWith(needle);
    }
    default: {
      return haystack === needle;
    }
  }
}

function entryMatches(entry: IGeographyEntry, term: string, kind: TextMatchKind): boolean {
  return textMatches(entry.code, term, kind) || (entry.name !== undefined && textMatches(entry.name, term, kind));
}

function isBlank(term: unknown): boolean {
  return term === null || term === undefined || term === '';
}

// A record matches a positive mode when any single entry's code or name matches; a negative
// mode is the exact inverse, so a record is excluded as soon as any entry matches.
export function matchesGeographyFilter(
  entries: IGeographyEntry[] | null | undefined,
  term: unknown,
  matchMode: string
): boolean {
  const kind = POSITIVE_KIND_BY_MODE[matchMode];
  if (isBlank(term) || !kind) {
    return true;
  }
  const anyMatch = (entries ?? []).some((entry) => entryMatches(entry, String(term), kind));
  return isNegativeGeographyMode(matchMode) ? !anyMatch : anyMatch;
}

export function getMatchRanges(text: string, term: string, kind: TextMatchKind): IMatchRange[] {
  const haystack = normalise(text);
  const needle = normalise(term);
  if (needle.length === 0 || !textMatches(text, term, kind)) {
    return [];
  }
  switch (kind) {
    case 'contains': {
      const ranges: IMatchRange[] = [];
      let index = haystack.indexOf(needle);
      while (index !== -1) {
        ranges.push({ start: index, end: index + needle.length });
        index = haystack.indexOf(needle, index + needle.length);
      }
      return ranges;
    }
    case 'startsWith': {
      return [{ start: 0, end: needle.length }];
    }
    case 'endsWith': {
      return [{ start: haystack.length - needle.length, end: haystack.length }];
    }
    default: {
      return [{ start: 0, end: haystack.length }];
    }
  }
}

function mergeRanges(ranges: IMatchRange[]): IMatchRange[] {
  const sorted = [...ranges].sort((a, b) => a.start - b.start);
  const merged: IMatchRange[] = [];
  for (const range of sorted) {
    const last = merged.at(-1);
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end);
    } else {
      merged.push({ ...range });
    }
  }
  return merged;
}

export interface IGeographyConstraint {
  value?: unknown;
  matchMode?: string;
}

export interface IEntryHighlight {
  nameMatches: boolean;
  nameRanges: IMatchRange[];
}

// Highlighting is driven only by active positive constraints, and only by name matches: a match by
// code alone never highlights. Negative constraints never highlight.
export function getEntryHighlight(entry: IGeographyEntry, constraints: IGeographyConstraint[]): IEntryHighlight {
  const nameRanges: IMatchRange[] = [];
  for (const constraint of constraints) {
    const { matchMode, value } = constraint;
    if (!matchMode || isBlank(value) || isNegativeGeographyMode(matchMode)) {
      continue;
    }
    const kind = POSITIVE_KIND_BY_MODE[matchMode];
    if (!kind) {
      continue;
    }
    if (entry.name !== undefined) {
      nameRanges.push(...getMatchRanges(entry.name, String(value), kind));
    }
  }
  return { nameMatches: nameRanges.length > 0, nameRanges: mergeRanges(nameRanges) };
}

export function splitByRanges(text: string, ranges: IMatchRange[]): { text: string; marked: boolean }[] {
  const parts: { text: string; marked: boolean }[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) {
      parts.push({ text: text.slice(cursor, range.start), marked: false });
    }
    parts.push({ text: text.slice(range.start, range.end), marked: true });
    cursor = range.end;
  }
  if (cursor < text.length) {
    parts.push({ text: text.slice(cursor), marked: false });
  }
  return parts;
}

for (const matchMode of Object.values(GEOGRAPHY_MATCH_MODES)) {
  FilterService.register(matchMode, (value: IGeographyEntry[], term: unknown) =>
    matchesGeographyFilter(value, term, matchMode)
  );
}
